import { useMemo } from 'react';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { useFigureWarnings } from '@/hooks/useFigureWarnings';
import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { mmToPx, pxToMm } from '@/lib/applyJournalTemplate';

interface StatusRow {
  id: string;
  tone: 'success' | 'warning' | 'info';
  label: string;
  detail: string;
  category: 'accessibility' | 'layout';
}

function StatusRow({ tone, label, detail }: { tone: StatusRow['tone']; label: string; detail: string }) {
  const bgClass = 
    tone === 'warning' 
      ? 'bg-amber-50/50 border-amber-200/50' 
      : tone === 'success'
      ? 'bg-green-50/30 border-green-200/30'
      : 'bg-muted/30 border-border/50';
  
  const textClass = 
    tone === 'warning'
      ? 'text-amber-700'
      : tone === 'success'
      ? 'text-green-700'
      : 'text-muted-foreground';

  const iconClass = 
    tone === 'warning'
      ? 'text-amber-600'
      : tone === 'success'
      ? 'text-green-600'
      : 'text-muted-foreground';

  const Icon = tone === 'warning' ? CircleAlert : tone === 'success' ? CircleCheck : Info;

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] ${bgClass}`}>
      <Icon className={`h-3 w-3 flex-shrink-0 ${iconClass}`} />
      <span className={`font-medium ${textClass}`}>{label}</span>
      <span className="text-muted-foreground">{detail}</span>
    </div>
  );
}

export function WarningsStrip() {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const warnings = useFigureWarnings(currentComposition);
  
  // Simple detection: check if any panel exceeds journal limit using grid calculations
  // Use the same calculation method as FigureCanvas to ensure consistency
  // Skip warning for default journal (no journal template selected)
  const panelsExceedLimit = useMemo(() => {
    if (!currentComposition || currentComposition.panels.length === 0) {
      return false;
    }
    
    // Don't warn if no journal template is selected (default journal)
    const journalTemplateId = currentComposition.theme?.journalTemplate;
    if (!journalTemplateId || journalTemplateId === 'none') {
      return false;
    }
    
    const grid = currentComposition.grid;
    const journalLimitWidthMm = currentComposition.canvasSize.width;
    const journalLimitHeightMm = currentComposition.canvasSize.height;
    
    // Convert journal limits to pixels (using 96 DPI, same as FigureCanvas)
    const journalLimitWidthPx = mmToPx(journalLimitWidthMm);
    const journalLimitHeightPx = mmToPx(journalLimitHeightMm);
    
    // Calculate base column width using journal limit (same as FigureCanvas)
    const baseColWidthPx = journalLimitWidthPx / grid.cols;
    const rowHeightPx = grid.rowHeight;
    const marginX = grid.margin[0];
    const marginY = grid.margin[1];
    
    // Check each panel using grid coordinates (same calculation as FigureCanvas)
    for (const panel of currentComposition.panels) {
      // Calculate panel position and size using base column width (same as FigureCanvas)
      const panelX = panel.x * (baseColWidthPx + marginX);
      const panelWidthPx = panel.w * baseColWidthPx + (panel.w - 1) * marginX;
      const panelRightPx = panelX + panelWidthPx;
      
      const panelY = panel.y * (rowHeightPx + marginY);
      const panelHeightPx = panel.h * rowHeightPx + (panel.h - 1) * marginY;
      const panelBottomPx = panelY + panelHeightPx;
      
      // Check if panel extends beyond journal limit
      if (panelRightPx > journalLimitWidthPx || panelBottomPx > journalLimitHeightPx) {
        return true;
      }
    }
    
    return false;
  }, [currentComposition?.panels, currentComposition?.grid, currentComposition?.canvasSize, currentComposition?.theme?.journalTemplate]);

  // Automatically calculate status results whenever composition changes
  const statusRows = useMemo<StatusRow[]>(() => {
    if (!currentComposition) return [];

    const rows: StatusRow[] = [];
    
    // Font size check
    const canvasWidthMm = currentComposition.canvasSize.width;
    const canvasHeightMm = currentComposition.canvasSize.height;
    const baseFontSizePt = 8;
    const fontScale = currentComposition.theme.fontScale ?? (currentComposition.theme.fontSize ? currentComposition.theme.fontSize / baseFontSizePt : 1.0);
    const effectiveBaseFontSizePt = baseFontSizePt * fontScale;
    const mmToPxAt300Dpi = (mm: number) => (mm / 25.4) * 300;
    const canvasWidthPx = mmToPxAt300Dpi(canvasWidthMm);
    const canvasHeightPx = mmToPxAt300Dpi(canvasHeightMm);
    const scaleFactor = Math.min(canvasWidthPx / 800, canvasHeightPx / 600);
    const effectiveFontSize = effectiveBaseFontSizePt * scaleFactor;
    
    if (effectiveFontSize < 6) {
      rows.push({
        id: 'font-size-small',
        tone: 'warning',
        label: 'Font size',
        detail: `~${effectiveFontSize.toFixed(1)} pt at print (min: 6 pt)`,
        category: 'accessibility',
      });
    } else {
      rows.push({
        id: 'font-size-ok',
        tone: 'success',
        label: 'Font size OK',
        detail: `~${effectiveFontSize.toFixed(1)} pt at print`,
        category: 'accessibility',
      });
    }

    // Canvas size check - show print size (journal limit) and warn if panels extend beyond it
    // Use same precision as ruler (1 decimal place) to match exactly
    const printWidthMm = canvasWidthMm;
    const printHeightMm = canvasHeightMm;
    
    // Simple check: do panels exceed the journal limit?
    if (panelsExceedLimit) {
      rows.push({
        id: 'canvas-exceeds-print',
        tone: 'warning',
        label: 'Canvas size',
        detail: `Print size: ${printWidthMm.toFixed(1)} × ${printHeightMm.toFixed(1)} mm (panels exceed limit)`,
        category: 'accessibility',
      });
    } else {
      rows.push({
        id: 'canvas-ok',
        tone: 'success',
        label: 'Canvas size OK',
        detail: `${printWidthMm.toFixed(1)} × ${printHeightMm.toFixed(1)} mm`,
        category: 'accessibility',
      });
    }

    // Colorblind-safe palette check - standardize to success/warning (no info tone)
    rows.push({
      id: 'colorblind',
      tone: currentComposition.theme.colorblindSafe ? 'success' : 'warning',
      label: 'Colorblind-safe palette',
      detail: currentComposition.theme.colorblindSafe ? 'On' : 'Off',
      category: 'accessibility',
    });

    // Scale mismatch check
    const scaleMismatchWarnings = warnings.filter(w => w.message.includes('different') && w.message.includes('range'));
    if (scaleMismatchWarnings.length > 0) {
      rows.push({
        id: 'scale-mismatch',
        tone: 'warning',
        label: 'Axis scales',
        detail: `${scaleMismatchWarnings.length} mismatch${scaleMismatchWarnings.length > 1 ? 'es' : ''}`,
        category: 'layout',
      });
    } else {
      rows.push({
        id: 'scale-ok',
        tone: 'success',
        label: 'Axis scales',
        detail: 'Consistent',
        category: 'layout',
      });
    }

    // Check for panels without plots
    const emptyPanels = currentComposition.panels.filter(p => !p.plotId);
    if (emptyPanels.length > 0) {
      rows.push({
        id: 'empty-panels',
        tone: 'info',
        label: 'Panels',
        detail: `${emptyPanels.length} panel${emptyPanels.length > 1 ? 's' : ''} empty (no plot)`,
        category: 'layout',
      });
    }

    return rows;
  }, [
    currentComposition, 
    warnings,
    currentComposition?.panels,
    currentComposition?.grid,
    currentComposition?.canvasSize,
    panelsExceedLimit,
  ]);

  const issueRows = statusRows.filter(r => r.tone === 'warning' || r.tone === 'info');
  const accessibilityRows = statusRows.filter(r => r.category === 'accessibility');
  const layoutRows = statusRows.filter(r => r.category === 'layout');
  const hasIssues = issueRows.length > 0 || warnings.length > 0;
  const allPass = !hasIssues;

  // Status icon to show in title when collapsed
  const StatusIcon = allPass ? CircleCheck : CircleAlert;
  const statusIconColor = allPass ? 'text-green-600' : 'text-amber-600';

  return (
    <>
      {/* Show regular warnings (from useFigureWarnings) */}
      {warnings.length > 0 && (
        <div className="space-y-1 mb-2">
          {warnings.map((warning) => (
            <div
              key={warning.id}
              className="flex items-start gap-1.5 px-2 py-1 rounded border bg-amber-50/50 border-amber-200/50 text-[11px]"
            >
              <CircleAlert className="h-3 w-3 mt-0.5 flex-shrink-0 text-amber-600" />
              <span className="text-amber-700">{warning.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Collapsible "Accessibility" section to view all results */}
      <Accordion type="single" collapsible className="w-full" defaultValue={undefined}>
        <AccordionItem value="accessibility-checks" className="border-none">
          <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
            <div className="flex items-center gap-2">
              <span>Accessibility</span>
              <StatusIcon className={`h-4 w-4 ${statusIconColor}`} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            {/* Show all check results when expanded */}
            <div className="space-y-1">
              {accessibilityRows.length > 0 && (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-1">
                    Accessibility
                  </p>
                  <div className="space-y-1 mb-2">
                    {accessibilityRows.map((row) => (
                      <StatusRow key={row.id} tone={row.tone} label={row.label} detail={row.detail} />
                    ))}
                  </div>
                </>
              )}

              {layoutRows.length > 0 && (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-1 mt-2">
                    Layout & Panels
                  </p>
                  <div className="space-y-1">
                    {layoutRows.map((row) => (
                      <StatusRow key={row.id} tone={row.tone} label={row.label} detail={row.detail} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}

